import { useState } from "react";
import { Link } from "react-router-dom";
import { PawLinkLogo } from "@/components/PawLinkLogo";
import { ArrowRight, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import Nav from "@/components/Nav";
import api from "@/lib/api";

export default function ForgotPassword() {
    const { t } = useI18n();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Endpoint always returns {ok:true} — we never leak whether the email exists.
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
        } catch { /* still show the neutral success screen */ }
        setLoading(false);
        setSent(true);
    };

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-paw-night">
            <Nav />
            <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-24 -start-24 w-[420px] h-[420px] rounded-full bg-paw-terra/10 dark:bg-paw-terra/[0.06] blur-[120px]" />
                <div className="absolute bottom-0 -end-32 w-[520px] h-[520px] rounded-full bg-paw-terra/8 dark:bg-paw-terra/[0.04] blur-[140px]" />
            </div>

            <div className="relative pt-24 sm:pt-28 pb-16 px-5 sm:px-6">
                <div className="w-full max-w-[400px] mx-auto">
                    <div className="text-center mb-8 sm:mb-10">
                        <Link to="/" className="inline-block mb-6 transition-transform hover:scale-[1.03]">
                            <PawLinkLogo variant="stacked" size={64} />
                        </Link>
                        <h1 className="font-display text-[32px] sm:text-[38px] leading-[1.1] font-medium tracking-tight text-paw-ink dark:text-white">
                            {sent ? t("auth.forgot_sent_title") : t("auth.forgot_title")}
                        </h1>
                        <p className="mt-2.5 text-[15px] text-paw-ink2 dark:text-paw-silver">
                            {sent ? t("auth.forgot_sent_sub") : t("auth.forgot_sub")}
                        </p>
                    </div>

                    <div className="card-soft rounded-3xl p-6 sm:p-8 space-y-5 shadow-hover" data-testid="forgot-password-form-wrap">
                        {sent ? (
                            <div className="flex flex-col items-center text-center py-6" data-testid="forgot-password-sent">
                                <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 grid place-items-center">
                                    <CheckCircle size={26} weight="fill" className="text-emerald-500" />
                                </div>
                                <p className="mt-4 text-sm text-paw-ink2 dark:text-paw-bone/70 leading-relaxed">
                                    <span className="font-mono text-paw-ink dark:text-white" dir="ltr">{email}</span>
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                                <div>
                                    <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                        <EnvelopeSimple size={12} weight="duotone" /> {t("auth.email")}
                                    </label>
                                    <input
                                        data-testid="forgot-password-email"
                                        type="email" required autoComplete="email"
                                        className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                        value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                    />
                                </div>
                                <button
                                    type="submit" disabled={loading}
                                    data-testid="forgot-password-submit"
                                    className="btn-primary w-full h-12 rounded-2xl text-[15px] tracking-tight disabled:opacity-60"
                                >
                                    {loading ? t("auth.forgot_sending") : t("auth.forgot_submit")}
                                    <ArrowRight size={16} className="ms-1.5" />
                                </button>
                            </form>
                        )}
                    </div>

                    <p className="text-center text-sm text-paw-ink2 dark:text-paw-bone/60 mt-6">
                        <Link to="/login" data-testid="forgot-password-back"
                            className="text-paw-forest dark:text-paw-terra font-semibold hover:underline underline-offset-4">
                            {t("auth.forgot_back_login")}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
